import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, {
  Mapped,
  Optional,
  Silent,
} from "@/__main__/data-model.mjs";
import { aggregatedAccount } from "@/data-models/league-account.mjs";
import { CHAMP_KEY_MAP } from "@/game-tft/constants.mjs";

const trait = {
  name: String,
  num_units: Number,
  style: Number,
  tier_current: Number,
  tier_total: Number,
} satisfies ModelDefinition;

const unit = {
  character_id: String,
  itemNames: [String],
  name: String,
  rarity: Number,
  tier: Number,
} satisfies ModelDefinition;

const participant = {
  gold_left: Number,
  last_round: Number,
  level: Number,
  placement: Number,
  players_eliminated: Number,
  time_eliminated: Number,
  total_damage_to_players: Number,
  missions: Mapped({ key: String, value: Number }),
  skill_tree: Optional(Mapped({ key: String, value: Number })),
  partner_group_id: Silent(Number),
  companion: {
    content_ID: String,
    item_ID: Number,
    skin_ID: Number,
    species: String,
  },
  BLITZ_riot_account: Silent(aggregatedAccount),
  augments: [String],
  traits: [trait],
  units: [unit],
  isAvg: Optional(Boolean),
  win: Optional(Boolean),
} satisfies ModelDefinition;

// This is meant to closely resemble the raw data from Riot, except
// encrypted IDs are removed since they are useless on the client, and
// League accounts are added.
// https://developer.riotgames.com/apis#tft-match-v1/GET_getMatch
const model = {
  metadata: {
    data_version: String,
    match_id: String,
    participants: [String],
  },
  info: {
    endOfGameResult: String,
    gameCreation: Date,
    game_datetime: Date,
    game_length: Number,
    game_version: String,
    queue_id: Number,
    tft_game_type: String,
    tft_set_core_name: String,
    tft_set_number: Number,
    participants: [participant],

    // Don't use these, these are just for completeness...
    gameId: Silent(Number),
    mapId: Silent(Number),
    queueId: Silent(Number),
  },

  // This is assigned by frontend only!
  _isLocal: Silent(Boolean),
} satisfies ModelDefinition;

const TFTMatchModel = createModel(model).transform(model, (data) => {
  for (const participant of data.info.participants) {
    for (const unit of participant.units) {
      if (CHAMP_KEY_MAP[unit.character_id as keyof typeof CHAMP_KEY_MAP]) {
        unit.character_id =
          CHAMP_KEY_MAP[unit.character_id as keyof typeof CHAMP_KEY_MAP];
      }
    }
  }

  return data;
});

export type TFTMatch = FromModel<typeof TFTMatchModel>;

export default TFTMatchModel;
